import React, { useState, useEffect, useRef } from "react";
import { projectURL } from "./utils/mockdata";
import CommentSound from "./audio/Comment.wav";

export const CreateComment = ({ postId, onCommentCreated }) => {
    const [newComment, setNewComment] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionRange, setMentionRange] = useState({ start: -1, end: -1 });
    const [activeIndex, setActiveIndex] = useState(0);
    const commentAudioRef = useRef(null);
    const textareaRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        commentAudioRef.current = new Audio(CommentSound);
    }, []);

    const onCommentSubmit = async (e) => {
        e.preventDefault();

        const userId = sessionStorage.getItem("loggedUserId");
        const userName = sessionStorage.getItem("loggedUserName");
        const commentPath = `${projectURL}comments/create`;
        const postPath = `${projectURL}posts/view/${postId}`;

        const dataInput = {
            postId,
            comment: newComment,
            created_by: userId,
        };

        try {
            // 1️⃣ Create the comment
            const commentResponse = await fetch(commentPath, {
                method: "POST",
                mode: "cors",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(dataInput),
            });

            if (!commentResponse.ok) {
                throw new Error(`Failed to create comment: ${commentResponse.status}`);
            }

            // Reset textarea and play sound
            setNewComment("");
            onCommentCreated?.();
            commentAudioRef.current?.play();

            // 2️⃣ Get post details (to find the post creator)
            const postResponse = await fetch(postPath, { mode: "cors" });
            if (!postResponse.ok) throw new Error("Error fetching post details");

            const postData = await postResponse.json();
            const postOwnerId = postData?.post?.created_by;

            // 3️⃣ Create a notification (you can later send this to your backend)
            const message = `${userName} commented on your post`;
            const dataNotification = {
                postId,
                message,
                from: userId,
                to: postOwnerId,
            };

            console.log("✅ Comment created successfully");
            console.log("📬 Notification Data:", dataNotification);

            fetch(`${projectURL}notification/create`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataNotification),
            }).then((res) => res.json())
                .then((data) => { console.log('Notification Data', data); });

        } catch (error) {
            console.error("❌ Error submitting comment:", error);
        }
    };

    // Handle input changes and detect @mentions
    const handleChange = (e) => {
        const val = e.target.value;
        const caret = e.target.selectionStart;
        setNewComment(val);

        const lastAt = val.lastIndexOf("@", caret - 1);
        if (lastAt !== -1 && (lastAt === 0 || /\s/.test(val[lastAt - 1]))) {
            const match = val.slice(lastAt + 1, caret);
            if (/^[\w]{0,20}$/.test(match)) {
                setMentionQuery(match);
                setMentionRange({ start: lastAt, end: caret });
                return;
            }
        }

        setMentionQuery("");
        setShowSuggestions(false);
        setSuggestions([]);
    };

    // Debounced fetch for suggestions
    useEffect(() => {
        if (!mentionQuery) return;
        const id = setTimeout(() => {
            fetch(`${projectURL}profiles/searchUsers?q=${encodeURIComponent(mentionQuery)}`)
                .then((res) => res.json())
                .then((data) => {
                    const users = data?.userList ?? [];
                    const names = users.map((u) => u.username).slice(0, 6);
                    setSuggestions(names);
                    setShowSuggestions(names.length > 0);
                    setActiveIndex(0);
                })
                .catch(() => {
                    setSuggestions([]);
                    setShowSuggestions(false);
                });
        }, 300);
        return () => clearTimeout(id);
    }, [mentionQuery]);

    // Click outside to close suggestions
    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const selectSuggestion = (username) => {
        const before = newComment.slice(0, mentionRange.start);
        const after = newComment.slice(mentionRange.end);
        const inserted = `@${username} `;
        const updated = before + inserted + after;
        setNewComment(updated);
        setMentionQuery("");
        setShowSuggestions(false);
        // restore focus and caret after inserted mention
        setTimeout(() => {
            textareaRef.current?.focus();
            const pos = (before + inserted).length;
            try {
                textareaRef.current.setSelectionRange(pos, pos);
            } catch (e) { }
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            // if mentions active, choose suggestion
            if (mentionQuery) {
                e.preventDefault();
                selectSuggestion(suggestions[activeIndex]);
            }
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    return (
        <div>
            <form
                onSubmit={onCommentSubmit}
                className="max-w-2sm bg-white rounded-lg mx-auto mt-2"
            >
                <span className="mb-1 mt-1 block" ref={wrapperRef}>
                    <textarea
                        ref={textareaRef}
                        value={newComment}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Comment"
                        className="block p-1.5 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        required
                    ></textarea>

                    {showSuggestions && suggestions.length > 0 && (
                        <ul className="absolute mt-2 bg-white border rounded shadow z-50 max-h-40 overflow-auto w-64 text-sm">
                            {suggestions.map((s, idx) => (
                                <li
                                    key={s}
                                    onClick={() => selectSuggestion(s)}
                                    className={`px-3 py-1 cursor-pointer hover:bg-gray-100 ${idx === activeIndex ? 'bg-gray-100' : ''}`}
                                >
                                    {s}
                                </li>
                            ))}
                        </ul>
                    )}
                </span>
                <span className="flex justify-center pt-2 px-2">
                    <button
                        type="submit"
                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-2 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-xs px-4 py-2 text-center mb-2"
                    >
                        Comment
                    </button>
                </span>
            </form>
        </div >
    );
};
